import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Paper, 
  Box, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';
import { 
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Computer as ComputerIcon,
  Smartphone as SmartphoneIcon,
  Tablet as TabletIcon,
  DeviceUnknown as DeviceUnknownIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { Session } from '../../services/authService';
import { format } from 'date-fns';

const SessionsPage: React.FC = () => {
  const { getSessions, terminateSession } = useAuth();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedSession, setSelectedSession] = useState<Session | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  
  const fetchSessions = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await getSessions();
      setSessions(data);
    } catch (err: any) {
      console.error('Error fetching sessions:', err);
      setError(err.response?.data?.message || 'Failed to load sessions');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchSessions();
  }, []);
  
  const handleRefresh = () => {
    fetchSessions();
  };
  
  const handleTerminate = (session: Session) => {
    setSelectedSession(session);
    setOpenDialog(true);
  };
  
  const confirmTerminate = async () => {
    if (!selectedSession) return;
    
    try {
      setLoading(true);
      await terminateSession(selectedSession.id);
      setSuccess(`Session from ${selectedSession.deviceInfo.deviceName || 'unknown device'} terminated successfully`);
      setOpenDialog(false);
      
      // Remove the terminated session from the list
      setSessions(sessions.filter(s => s.id !== selectedSession.id));
    } catch (err: any) {
      console.error('Error terminating session:', err);
      setError(err.response?.data?.message || 'Failed to terminate session');
    } finally {
      setLoading(false);
    }
  };
  
  const getDeviceIcon = (session: Session) => {
    const deviceType = session.deviceInfo.deviceName?.toLowerCase() || '';
    
    if (deviceType.includes('iphone') || deviceType.includes('android') || deviceType.includes('mobile')) {
      return <SmartphoneIcon />;
    } else if (deviceType.includes('ipad') || deviceType.includes('tablet')) {
      return <TabletIcon />;
    } else if (deviceType.includes('mac') || deviceType.includes('windows') || deviceType.includes('linux')) {
      return <ComputerIcon />;
    } else {
      return <DeviceUnknownIcon />;
    }
  };
  
  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MMM d, yyyy h:mm a');
    } catch (e) {
      return 'Invalid date';
    }
  };
  
  const getCurrentDeviceId = () => {
    return localStorage.getItem('device_id');
  };
  
  const isCurrentDevice = (session: Session) => {
    return session.deviceInfo.deviceId === getCurrentDeviceId();
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" component="h1">
            Active Sessions
          </Typography>
          <Button 
            startIcon={<RefreshIcon />} 
            onClick={handleRefresh}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {sessions.length === 0 ? (
              <Alert severity="info">No active sessions found.</Alert>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Device</TableCell>
                      <TableCell>Browser</TableCell>
                      <TableCell>Operating System</TableCell>
                      <TableCell>IP Address</TableCell>
                      <TableCell>Last Used</TableCell>
                      <TableCell>Expires</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sessions.map((session) => (
                      <TableRow 
                        key={session.id}
                        sx={{ 
                          backgroundColor: isCurrentDevice(session) ? 'rgba(25, 118, 210, 0.08)' : 'inherit'
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {getDeviceIcon(session)}
                            <Typography sx={{ ml: 1 }}>
                              {session.deviceInfo.deviceName || 'Unknown Device'}
                              {isCurrentDevice(session) && (
                                <Typography component="span" sx={{ ml: 1, fontSize: '0.75rem', color: 'primary.main' }}>
                                  (Current)
                                </Typography>
                              )}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{session.deviceInfo.browser || 'Unknown'}</TableCell>
                        <TableCell>{session.deviceInfo.os || 'Unknown'}</TableCell>
                        <TableCell>{session.deviceInfo.ip || 'Unknown'}</TableCell>
                        <TableCell>
                          {session.deviceInfo.lastUsed ? formatDate(session.deviceInfo.lastUsed) : 'Never'}
                        </TableCell>
                        <TableCell>{formatDate(session.expiresAt)}</TableCell>
                        <TableCell align="center">
                          <Tooltip title="Terminate Session">
                            <IconButton 
                              color="error" 
                              onClick={() => handleTerminate(session)}
                              disabled={isCurrentDevice(session)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </Paper>
      
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
      >
        <DialogTitle>Terminate Session</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to terminate the session from 
            {selectedSession?.deviceInfo.deviceName ? ` ${selectedSession.deviceInfo.deviceName}` : ' this device'}?
            This will immediately log out that device.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={confirmTerminate} color="error" autoFocus>
            Terminate
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SessionsPage;
